import React from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { ThemeSelector, useTheme } from '@/themes';
import { Palette, CheckCircle2, Sun, Moon, Sparkles } from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';

export const ThemesView: React.FC = () => {
  const { currentTheme, availableThemes } = useTheme();

  return (
    <div className="space-y-6 animate-fade-in pb-12">
      {/* Top Header */}
      <PageHeader
        title="Terminal Themes & Appearance"
        description="Select a custom trading desk visual theme. Applied instantly across all workspace views and preserved locally on this device."
        icon={Palette}
        badge={
          <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-[var(--accent-muted)] border border-[var(--border-default)] text-[var(--accent-primary)]">
            <Palette className="w-3.5 h-3.5" />
            <span>{availableThemes.length} THEMES AVAILABLE</span>
          </span>
        }
      />

      {/* Active Theme Summary Card */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-5 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-[var(--border-subtle)] pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-lg bg-[var(--accent-muted)] border border-[var(--border-default)] text-[var(--accent-primary)]">
              {currentTheme.type === 'light' ? (
                <Sun className="w-5 h-5 text-amber-500" />
              ) : (
                <Moon className="w-5 h-5 text-[var(--accent-primary)]" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-mono font-bold text-[var(--text-primary)]">
                  {currentTheme.name}
                </h2>
                <span className="px-2 py-0.5 rounded text-[10px] font-mono uppercase font-bold bg-[var(--accent-muted)] text-[var(--accent-primary)] border border-[var(--border-subtle)]">
                  {currentTheme.type.toUpperCase()} MODE
                </span>
                {currentTheme.isDefault && (
                  <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-950/60 text-emerald-400 border border-emerald-800/40">
                    DEFAULT
                  </span>
                )}
              </div>
              <p className="text-xs font-mono text-[var(--text-muted)] mt-0.5">
                {currentTheme.description}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-mono font-bold bg-emerald-600 text-white shadow-sm">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>CURRENTLY ACTIVE</span>
            </span>
          </div>
        </div>

        {/* Live Palette Swatches */}
        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2.5 text-[11px] font-mono">
          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.bgApp }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Background</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.bgApp}</div>
            </div>
          </div>

          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.bgSurface }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Surface</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.bgSurface}</div>
            </div>
          </div>

          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.borderDefault }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Border</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.borderDefault}</div>
            </div>
          </div>

          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.textPrimary }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Primary Text</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.textPrimary}</div>
            </div>
          </div>

          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.accentPrimary }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Primary Accent</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.accentPrimary}</div>
            </div>
          </div>

          <div className="p-2 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] flex items-center gap-2">
            <span
              className="w-3.5 h-3.5 rounded border border-black/20 shrink-0"
              style={{ backgroundColor: currentTheme.colors.accentSecondary }}
            />
            <div className="truncate">
              <div className="text-[9px] text-[var(--text-dim)] uppercase">Secondary Accent</div>
              <div className="text-[var(--text-secondary)] font-semibold truncate">{currentTheme.colors.accentSecondary}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Theme Selector Grid */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-6 space-y-4">
        <ThemeSelector />
      </div>

      {/* Component Visual Harmony Showcase */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border-default)] rounded-xl p-4 sm:p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-[var(--border-subtle)] pb-3">
          <Sparkles className="w-4 h-4 text-[var(--accent-primary)]" />
          <h3 className="font-mono font-bold text-sm text-[var(--text-primary)]">
            Live Component Harmony Showcase
          </h3>
          <span className="text-[11px] font-mono text-[var(--text-muted)]">
            (Verify how telemetry cards and indicators render in {currentTheme.name})
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-3.5 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] space-y-1.5">
            <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase block">Long Entry</span>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-sm text-[var(--text-primary)]">RELIANCE</span>
              <StatusBadge status="POSITION_OPENED" label="OPEN" size="sm" />
            </div>
            <div className="text-xs font-mono text-emerald-500 font-bold">+₹2,450.00 (+1.82%)</div>
          </div>

          <div className="p-3.5 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] space-y-1.5">
            <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase block">Execution Status</span>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-sm text-[var(--text-primary)]">TCS</span>
              <StatusBadge status="COMPLETED" label="TARGET HIT" size="sm" />
            </div>
            <div className="text-xs font-mono text-emerald-500 font-bold">+₹4,200.00 (+2.50%)</div>
          </div>

          <div className="p-3.5 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] space-y-1.5">
            <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase block">Risk Boundary</span>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-sm text-[var(--text-primary)]">CIRCUIT BREAKER</span>
              <StatusBadge status="HEALTHY" label="NORMAL" size="sm" />
            </div>
            <div className="text-xs font-mono text-[var(--text-secondary)]">₹0 / ₹2,000 Daily Cap</div>
          </div>

          <div className="p-3.5 rounded-lg bg-[var(--bg-surface-elevated)] border border-[var(--border-default)] space-y-1.5">
            <span className="text-[10px] font-mono text-[var(--text-muted)] uppercase block">Telegram Signal</span>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-sm text-[var(--text-primary)]">INFY</span>
              <StatusBadge status="PENDING" label="VALIDATED" size="sm" />
            </div>
            <div className="text-xs font-mono text-[var(--accent-primary)] font-semibold">Ready for CMP Fill</div>
          </div>
        </div>

        {/* BUY & SELL Card Telemetry Harmony */}
        <div className="space-y-2 pt-3 border-t border-[var(--border-subtle)]">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono text-[var(--text-muted)] uppercase tracking-wider font-semibold">
              BUY & SELL Card Visual Distinction
            </span>
            <span className="text-[10px] font-mono text-[var(--text-dim)]">
              (Theme-Aware Cards: Subtle Tint, Contrast Borders & Readable Text)
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-3.5 rounded-lg border card-trade-buy space-y-2 interactive-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm text-[var(--text-primary)]">TATAMOTORS</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase border bg-emerald-950/70 text-emerald-400 border-emerald-800/60">
                    BUY
                  </span>
                </div>
                <span className="text-xs font-mono text-emerald-500 font-bold">+₹1,850.00 (+1.45%)</span>
              </div>
              <div className="text-[11px] font-mono text-[var(--text-muted)] flex justify-between border-t border-slate-800/60 pt-2">
                <span>QTY: 150 @ ₹980.50</span>
                <span>ENTRY FILLED</span>
              </div>
            </div>

            <div className="p-3.5 rounded-lg border card-trade-sell space-y-2 interactive-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-bold text-sm text-[var(--text-primary)]">HDFCBANK</span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase border bg-rose-950/70 text-rose-400 border-rose-800/60">
                    SELL
                  </span>
                </div>
                <span className="text-xs font-mono text-rose-400 font-bold">-₹620.00 (-0.78%)</span>
              </div>
              <div className="text-[11px] font-mono text-[var(--text-muted)] flex justify-between border-t border-slate-800/60 pt-2">
                <span>QTY: 50 @ ₹1,585.00</span>
                <span>STOP-LOSS EXIT</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
